import * as Sentry from "@sentry/react";

type SentryUserInfo = {
  id: string;
  email: string;
};

// OMG-2134: tracks whether the identity currently on the Sentry scope is an
// anonymous checkout-buyer email WE set (vs the authenticated identity
// NmhContext owns). Leaving checkout must clear the former but never the
// latter. Kept here rather than in a component ref because two call sites set
// the anon identity — the typed-email effect in checkout.tsx and the wallet
// path in BraintreeCheckout — and only a shared flag lets either be cleared
// consistently on unmount.
let anonBuyerIdentitySet = false;

export const setSentryUser = ({ id, email }: SentryUserInfo) => {
  Sentry.setUser({
    id,
    email,
  });
  // A full authenticated identity now owns the scope, so there's no longer an
  // anonymous buyer identity for checkout cleanup to clear (covers the buyer
  // who typed an email then authenticated mid-flow).
  anonBuyerIdentitySet = false;
};

export const clearSentryUser = () => {
  Sentry.setUser(null);
  anonBuyerIdentitySet = false;
};

// OMG-2134: correlate a checkout session to its buyer so a CX-reported email
// resolves to that session's Sentry events + 100%-sampled replay in seconds.
// Only for the logged-out checkout buyer — authenticated identity is owned by
// NmhContext (id + email), which we must not clobber with an email-only user.
export const setCheckoutBuyerEmail = (email: string) => {
  Sentry.setUser({ email });
  anonBuyerIdentitySet = true;
};

// OMG-2134: clear an anonymous checkout-buyer identity on leaving checkout, no
// matter which path set it (typed email or wallet payload). No-op if we never
// set one, so it can't clobber the authenticated identity NmhContext owns.
export const clearCheckoutBuyerIdentity = () => {
  if (!anonBuyerIdentitySet) return;
  Sentry.setUser(null);
  anonBuyerIdentitySet = false;
};

// OMG-2134: tag the current scope (and thus the ongoing replay) with the
// checkout attempt's idempotency key, so a Sentry event / replay joins the
// checkout_submitted / _result analytics events 1:1 on the same key. Mirrored
// onto the omg-api error scope so FE and BE errors share the tag.
export const tagCheckoutAttempt = (checkoutAttempt: string) => {
  Sentry.setTag("checkout_attempt", checkoutAttempt);
};

// Drop the checkout_attempt tag when leaving checkout so it doesn't ride along
// on later, unrelated events/replays (undefined tags are omitted from events).
export const clearCheckoutAttempt = () => {
  Sentry.setTag("checkout_attempt", undefined);
};

// Suppress events we've triaged as non-app errors so they don't count against
// our monthly Sentry quota. Each entry is confirmed via triage (see
// claude_docs/sentry-triage-2026-04-24.md — intentionally untracked, lives in
// the shared claude_docs directory, not this repo) against either code that doesn't
// originate from our app (browser extensions, WebKit internals, ad-blocked
// third parties) or user-controlled transient failures (navigation aborts,
// CAPTCHA friction, flaky networks). If a pattern here ever represents a
// real app bug, add a narrower capture at the call site instead of removing
// the pattern wholesale.
const IGNORE_ERRORS = [
  // Browser / extension / WebKit internals (not app code)
  /Can't find variable: EmptyRanges/,
  /feature named .+ was not found/,
  /Promise rejection value is a non-unwrappable cross-compartment/,

  // In-app-browser / WebView internals, not app code (OMG-2241):
  // window.webkit.messageHandlers is probed by injected scripts outside a
  // real WebView; "Java object is gone" is Android WebView bridge teardown
  // during navigation (its "Error invoking postMessage" variant always
  // carries this message too, so one pattern covers both).
  /window\.webkit\.messageHandlers/,
  /Java object is gone/,
  // iOS WKWebView bridge (DuckDuckGo and other in-app browsers): an unhandled
  // rejection with no stack, thrown by the HOST APP's injected bridge. It
  // cannot be ours: `grep -rn postMessage src` and `grep -rn messageHandlers
  // src` both return zero callers outside this file, so nothing in this app
  // speaks that bridge (NMH-REACT-CWQ, 131 events / 110 users, missed in the
  // 7/22 sweep; OMG-2249).
  /WKWebView API client did not respond to this postMessage/,

  // Private-mode / sandboxed / WebView storage access denied (not app code).
  // Distinct from the "Indexed Database server lost" transient below.
  //
  // NOTE (OMG-2249): until this change these patterns ALSO hid a first-party
  // throw — unguarded localStorage reads that unmounted the app for
  // storage-blocked browsers (NMH-REACT-V / -2BW). Every storage access in the
  // app now goes through lib/safeStorage.ts and cannot throw, INCLUDING the
  // two the first sweep missed (a bare `localStorage` in a LoginForm effect
  // dependency array, and the chunk-reload guard in routes.tsx) — so what
  // these patterns still match is third-party.
  //
  // Evidence they work, rather than an assumption: Convert's indexedDB probe
  // (NMH-REACT-CXW) ran 241 events to 2026-08-02 and has been silent since,
  // which is when the 7/22 sweep that added these strings reached prod. If a
  // first-party storage error ever needs capturing again, capture it at the
  // call site; these strings cannot be narrowed by message (Safari's carries
  // no storage word at all).
  /Can't find variable: indexedDB/,
  /indexedDB is not defined/,
  /Access is denied for this document/,
  /The operation is insecure/,

  // Google Pay unavailable (ad-blocker / unsupported context): the pay.js
  // script fails to load, and the support probe then errors. Both are the
  // same root cause; checkout falls back to card entry, so neither is an app
  // failure. denyUrls can't catch the load failure (it's thrown from our
  // bundle, not from pay.google.com), so match the message here too.
  /pay\.google\.com\/.*failed to load/,
  /googlePay view errored/,

  // PayPal wallet sub-component fails to connect (OMG-2135, closed won't-fix):
  // Braintree logs this via console.error during drop-in init; the drop-in
  // still resolves and renders without PayPal, and card checkout stays
  // available. Deliberately narrower than "error creating Drop-in" — that one
  // is a real full-form failure and must keep reporting.
  /There was an error connecting to PayPal/,

  // Ad-blocker / third-party script load failures
  /googletagmanager\.com/,
  /gtag\/js/,

  // Segment analytics.js logs console.error when a destination action fails to
  // initialize (e.g. its Google Analytics 4 Web destination when GA4 is
  // ad-blocked). captureConsoleIntegration turns that into a Sentry event
  // (NMH-REACT-5Y, ~7k events / 1k users). Third-party noise, not app code —
  // we can't force GA4 past an ad-blocker, and GA4 coverage is monitored in
  // GA4 itself, not per-event in Sentry.
  /Failed to create PluginFactory/,

  // User-initiated aborts / transient network
  /^Load failed$/,
  /^Failed to fetch$/,
  /^NetworkError when attempting to fetch resource/,
  /AbortError: The operation was aborted/,
  /AbortError: The fetching process for the media resource/,
  /UnknownError: Connection to Indexed Database server lost/,

  // Cloudflare Turnstile challenge failures (user-side, tracked elsewhere)
  /Cloudflare Turnstile.*Error: (110|300)/,

  // braintree-web-drop-in's Apple Pay teardown calls ApplePaySession.abort()
  // on a session that's already completed/aborted, which rejects with this
  // DOMException as an unhandled rejection (NMH-REACT-CZ6, 110 users). Twice
  // replay-confirmed to fire on SUCCESSFUL purchases — pure teardown noise,
  // and the single noisiest error on /checkout, where it pollutes the signal
  // checkout triage now relies on (OMG-2132). Anchored on the type prefix —
  // the SDK's inbound filter tests both `value` and `type: value`, and the
  // anchored form matches only the latter — so an error of a DIFFERENT type
  // that happens to share this message string is still captured. Known
  // trade-off: this suppresses every InvalidAccessError with this message
  // regardless of origin; triage found no others in the current dataset. If
  // a real one ever appears, capture it explicitly at the call site per the
  // note above.
  /InvalidAccessError: The object does not support the operation or argument/,
];

// Drop events sourced from third-party scripts we don't control.
// pay.google.com is belt-and-braces only: the observed Google Pay failures
// (NMH-REACT-CZ4) throw from our own bundle, so denyUrls can't match them —
// the ignoreErrors message patterns above are what actually suppress those.
//
// DELIBERATELY NOT HERE: convertexperiments.com. An earlier draft of OMG-2249
// added it, justified by Convert's indexedDB probe (NMH-REACT-CXW). Checking
// that against Sentry rather than assuming it retired the entry on both
// counts: CXW is already suppressed by the indexedDB message patterns above
// and has been silent since 2026-08-02, so the entry buys nothing; and a host
// pattern is not message-scoped, so it would also have silenced
// NMH-REACT-D0Y — a LIVE Convert error, 145 events, last seen 2026-09-09.
// Convert is what assigns arms for a running revenue experiment, so blanket
// denying its bundle would blind us to the failure mode that matters most
// there. If a specific Convert error needs suppressing, match its message.
const DENY_URLS = [/googletagmanager\.com/, /clarity\.ms/, /pay\.google\.com/];

// Belt for the simulation engine (OMG-1709). `captureConsoleIntegration` turns
// every console.error into a Sentry error event, and the ported engine logs
// diagnostics that way — including one on the pointer chain, so a single touch
// on a transition region minted an event. The fix is that engine diagnostics now
// go through `simWarn` (engine/utils.js), which logs at warn level and is not
// captured; this filter only catches a future `console.error` that reaches for
// the same `[sim]` prefix. It deliberately does not touch events carrying a real
// exception — the engine's actual failures are reported by useSimulation's error
// funnel with a phase and the error object attached, and those must keep flowing:
// they are the signal the OMG-2277 rollout reads to spot a bad cohort.
const isSimConsoleNoise = (event: Sentry.ErrorEvent) =>
  !event.exception?.values?.length &&
  typeof event.message === "string" &&
  event.message.startsWith("[sim]");

export const initializeSentry = () => {
  if (!["dev", "local"].includes(import.meta.env.VITE_ENV)) {
    // Checkout is always the entry route when users land from the marketing
    // site, so we can decide the replay sample rate at boot. Record 100% of
    // checkout sessions while the conversion drop is being investigated; keep
    // the original 1% everywhere else to stay within Sentry quota.
    const path = window.location.pathname;
    const isCheckoutEntry =
      path === "/checkout" || path.startsWith("/checkout/");

    // OMG-2134: the omg-api host is a distinct origin per stage, so sampled
    // traces stopped at the FE until the configured base URL was added below.
    // Since OMG-2539 the hosts are api.omgyes.com / api.uat.omgyes.com and the
    // regex fallback covers them too, so an unset env var no longer silently
    // drops FE→BE trace propagation (965 review).
    const omgApiUrl = import.meta.env.VITE_OMG_API_URL as string | undefined;

    Sentry.init({
      dsn: "https://1f7def9cfd5ef6acbb4412213b120b68@o4504329267445760.ingest.us.sentry.io/4508140148948992",
      // Session Replay is deliberately NOT in this list: it is attached
      // below via lazyLoadIntegration once the app is booting. See there.
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        Sentry.captureConsoleIntegration({ levels: ["error"] }),
      ],
      environment: import.meta.env.VITE_ENV,
      ignoreErrors: IGNORE_ERRORS,
      denyUrls: DENY_URLS,
      beforeSend: (event) => (isSimConsoleNoise(event) ? null : event),
      // Tracing
      tracesSampleRate: 0.01,
      profilesSampleRate: 0.1,
      tracePropagationTargets: [
        "localhost",
        // The api.* omg-api hosts (prod and UAT). Deliberately NOT a bare
        // *.omgyes.com wildcard: adding sentry-trace headers to requests at
        // videos.omgyes.com would flunk that CDN's CORS allowlist.
        /^https:\/\/api(?:\.uat)?\.omgyes\.com\//,
        ...(omgApiUrl ? [omgApiUrl] : []),
      ],
      // Session Replay. Read by the replay integration when it is attached
      // below, so these stay here even though the integration itself is
      // not in the `integrations` list.
      replaysSessionSampleRate: isCheckoutEntry ? 1.0 : 0.01,
      replaysOnErrorSampleRate: isCheckoutEntry ? 1.0 : 0.01,
    });

    // Session Replay off the critical path (OMG-1836). Bundled statically,
    // the replay integration (rrweb recorder + its plumbing) was 125 KB raw
    // of the 410 KB entry chunk: 39 KB gzip on every route, measured by
    // building with and without it. Outside /checkout the sample rate is
    // 1%, so 99% of member, login and gift visitors were downloading and
    // initialising a recorder that never starts.
    //
    // Sentry's own lazy loader fetches replay.min.js for this exact SDK
    // version from browser.sentry-cdn.com (47 KB gzip, 1y cache, CORS) as
    // an async <script> and resolves with the integration factory; the
    // bundle no longer contains the recorder at all. The sample rates above
    // are read from the client options when the integration is attached,
    // so the 100% checkout / 1% elsewhere split is unchanged, and so is the
    // quota it consumes.
    //
    // Trade-off: recording starts when the script lands rather than at
    // init, a few hundred ms later on a cold mobile load. On /checkout that
    // gap is the inline skeleton; an error thrown inside it reaches Sentry
    // without a replay attached. index.html preconnects to the CDN on
    // checkout paths to keep that gap short.
    //
    // A rejection (ad blocker, CDN outage) is swallowed on purpose: errors
    // still report, the session simply has no replay. Reporting the
    // failure itself would mint one event per blocked visitor.
    Sentry.lazyLoadIntegration("replayIntegration")
      .then((replayIntegration) => {
        // maskAllText is off so checkout copy is legible in replays, but
        // maskAllInputs must stay on: payment-first seeds the set-password
        // form with the generated dummy credential, and checkout sessions
        // sample Replay at 100%. Never let an input value (password or
        // email) reach Sentry (P0, payment-first).
        Sentry.addIntegration(
          replayIntegration({ maskAllText: false, maskAllInputs: true }),
        );
      })
      .catch(() => {});
  }
};
